import { useId, useRef, type ReactNode } from 'react';

import { useFocusTrap, useOnEscape } from '../lib/hooks';

import { IconButton } from './IconButton';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** Accessible dialog name; also shown in the header when `header` is omitted. */
  title: string;
  /** Custom header content (badge + title + action line). */
  header?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * A right-edge drawer (a11y §8.6.6): focus trap, Escape and scrim-click to close,
 * `role="dialog"` + `aria-modal`, labelled title, focus restore. The scrim is a
 * real button so assistive tech can dismiss it too.
 */
export function Drawer({ open, onClose, title, header, children, footer }: DrawerProps): React.JSX.Element | null {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  useFocusTrap(ref, open);
  useOnEscape(open, onClose);

  if (!open) return null;

  return (
    <>
      <button type="button" className="ds-drawer-scrim" aria-label="Close drawer" onClick={onClose} />
      <div ref={ref} className="ds-drawer" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="ds-drawer__header">
          {header ?? (
            <h2 id={titleId} className="ds-drawer__title">
              {title}
            </h2>
          )}
          {header !== undefined ? <span id={titleId} className="sr-only">{title}</span> : null}
          <IconButton label="Close" tone="bare" onClick={onClose} style={{ marginLeft: 'auto' }}>
            <span aria-hidden="true">✕</span>
          </IconButton>
        </div>
        <div className="ds-drawer__body">{children}</div>
        {footer !== undefined ? <div className="ds-drawer__footer">{footer}</div> : null}
      </div>
    </>
  );
}
